import { CustomTabWithStep } from '@/components/ats/Tabs';
import { useShowToast } from '@/hooks';
import {
  useCancelScheduledAssessmentMutation,
  useScheduleAssessmentMutation,
  useSendAssessmentMutation,
  useUpdateAssessmentMutation,
} from '@/store/query/assessment';
import {
  Button,
  Flex,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { isEqual } from 'lodash-es';
import { Details } from './Details';
// ! assessment commenting for V1
// import { Email } from './Email';
import { Scorecard } from './ScoreCard';
import { ModalWrapper } from '@/components/ats/modal';
import { assessmentInternalErrorMessages } from '../../utils';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { useSelector } from 'react-redux';
import { ScheduleForLater } from '../schedule/ScheduleForLater';
import {
  handleCancelSchedule,
  handleScheduleSubmit,
} from '../schedule/SchedulingServices';

export const Assessment = ({
  isAdhoc = false,
  onClose = () => {},
  assessmentData,
  refetch,
  refetchAssessmentData,
  onCreateAndSend = null,
}) => {
  const { currentJob } = useSelector(getAtsJobCreate);
  const currentJobData = currentJob?.data || {};
  let defaultScorecardParams;
  const { department, office, employment_type } =
    currentJobData?.basic_info || {};
  if (currentJobData?.basic_info) {
    defaultScorecardParams = {
      department_ids: [department.id],
      office_ids: office?.map((itr) => itr.id),
      employment_types: [employment_type],
    };
  }

  const [currentTab, setCurrentTab] = useState('details');
  const [tabVisited, setTabVisited] = useState([]);
  const [scorecardRequired, setScorecardRequired] = useState(
    assessmentData?.scorecard_required ?? true
  );

  const isCreateFlow =
    assessmentData?.status === 'pending' || !assessmentData?.id;
  const detailsRef = useRef(null);
  const scorecardRef = useRef(null);
  const scheduleRef = useRef(null);
  const emailRef = useRef(null);
  const toast = useShowToast();
  const confirmActions = useDisclosure();

  const [sendAssessment, { isLoading: isSending }] =
    useSendAssessmentMutation();
  const [scheduleAssessment, { isLoading: isScheduling }] =
    useScheduleAssessmentMutation();
  const [updateAssessment, { isLoading: isUpdating }] =
    useUpdateAssessmentMutation();
  const [cancelScheduledAssessment, { isLoading: isCancelScheduled }] =
    useCancelScheduledAssessmentMutation();

  const assessmentTabs = [
    {
      id: 'details',
      label: 'Details',
      component: Details,
      props: {
        formRef: detailsRef,
        isAdhoc,
      },
    },
    {
      id: 'scorecard',
      label: 'Scorecard',
      component: Scorecard,
      props: {
        formRef: scorecardRef,
        questions: assessmentData?.scorecard?.questions?.length
          ? assessmentData?.scorecard?.questions
          : [],
        scorecardData: assessmentData?.scorecard,
        defaultScorecardParams,
        toggleLabel: 'Include scorecards in this assessment',
        scorecardRequired,
        onScorecardRequiredChange: setScorecardRequired,
      },
    },
    // ! assessment commenting for V1
    // {
    //   id: 'email',
    //   label: 'Email',
    //   component: Email,
    //   props: {
    //     formRef: emailRef,
    //   },
    // },
  ];

  const getNextTab = () => {
    const currentIndex = assessmentTabs.findIndex(
      (tab) => tab.id === currentTab
    );
    return assessmentTabs?.[currentIndex + 1];
  };

  const getEmailHasError = () => {
    const { subject, body, from } = emailRef?.current?.values || {};

    if (!subject || !from || !body || !body?.includes('{{assessment.link}}'))
      return true;
    else return false;
  };

  const handleModalSubmit = async () => {
    try {
      const params = {
        ...detailsRef?.current?.values,
        ...(scorecardRequired ? scorecardRef?.current?.values : {}),
        scorecard_required: scorecardRequired,
        content_updated: true,
      };
      const data = await updateAssessment({
        payload: { assessmentId: assessmentData?.id, params },
      }).unwrap();

      if (data?.id) {
        toast({
          title: 'Assessment updated successfully',
          status: 'success',
        });
        refetch();
        onClose();
      } else {
        throw new Error('Updating assessment failed');
      }
    } catch (error: unknown) {
      toast({
        title: 'Updating assessment failed',
        status: 'error',
        description:
          error instanceof Error ? error.message : 'Updating assessment failed',
      });
    }
  };

  const shouldShowConfirmation = () => {
    if (!assessmentData) return false;

    const { text, attachments } = assessmentData;
    const { assessment_upload: newAttachments, assessment: newText } =
      detailsRef?.current?.values || {};

    return (
      [
        'sent',
        'sending_failed',
        'sending_in_progress',
        'submission_in_progress',
      ].includes(assessmentData.status) &&
      (!isEqual(text, newText) || !isEqual(attachments, newAttachments))
    );
  };

  const handleSubmissionResult = (result) => {
    if (result?.data?.id) {
      toast({
        title: `Assessment ${!isCreateFlow ? 'updated' : 'sent'} successfully`,
        status: 'success',
      });
      refetch();
      onClose();
    } else {
      throw new Error(
        result?.error?.[0] ||
          `${!isCreateFlow ? 'Updating' : 'Sending'} assessment failed`
      );
    }
  };

  const handleAssessmentSubmit = async () => {
    try {
      // Validate forms
      await detailsRef.current?.submitForm();
      if (scorecardRequired) {
        await scorecardRef.current?.submitForm();
      }

      const isScorecardValid = scorecardRequired
        ? scorecardRef.current?.isValid
        : true;
      if (!detailsRef.current?.isValid || !isScorecardValid) {
        return;
      }

      const params = {
        ...detailsRef.current.values,
        ...(scorecardRequired ? scorecardRef.current.values : {}),
        scorecard_required: scorecardRequired,
      };

      // Handle ad-hoc case
      if (isAdhoc && !assessmentData?.id) {
        return onCreateAndSend(params);
      }

      // Handle standard flow
      let result;
      if (isCreateFlow) {
        result = await sendAssessment({
          payload: { assessmentId: assessmentData?.id, params },
        });
      } else if (shouldShowConfirmation()) {
        confirmActions.onOpen();
        return;
      } else {
        result = await updateAssessment({
          payload: { assessmentId: assessmentData?.id, params },
        });
      }

      handleSubmissionResult(result);
    } catch (error) {
      toast({
        title:
          assessmentInternalErrorMessages?.[error?.[0]] ||
          (error?.[0]?.includes(':') && error?.[0]?.split(':')[1]) ||
          `${!isCreateFlow ? 'Updating' : 'Sending'} assessment failed`,
        status: 'error',
        description: error?.[0]
          ? error?.[0]?.includes(':')
            ? error?.[0]?.split(':')[1]?.trim()
            : error?.[0]
          : '',
      });
    }
  };

  const clickHandler = async () => {
    const currentTabObj = assessmentTabs.find((tab) => tab.id === currentTab);

    // Validate current tab (skip scorecard validation when toggle is off)
    const isScorecardSkippable =
      currentTabObj.id === 'scorecard' && !scorecardRequired;
    if (!isScorecardSkippable) {
      await currentTabObj?.props?.formRef?.current?.submitForm();
      if (!currentTabObj?.props?.formRef?.current?.isValid) return;
    }

    if (currentTabObj.id === 'scorecard') {
      return handleAssessmentSubmit();
    }

    // Proceed to next tab
    setTabVisited((prev) => [...prev, currentTabObj.id]);
    setCurrentTab(getNextTab()?.id);
  };

  const handleScheduleAction = async (scheduleData) => {
    try {
      await detailsRef.current?.submitForm();
      if (scorecardRequired) {
        await scorecardRef.current?.submitForm();
      }

      const isScorecardValid = scorecardRequired
        ? scorecardRef.current?.isValid
        : true;
      if (!detailsRef.current?.isValid || !isScorecardValid) {
        return false;
      }

      const scheduleAt = scheduleData.schedule_at;
      if (!scheduleAt?.formattedTime || !scheduleAt?.timezoneValue?.key) {
        toast({
          title: 'Invalid schedule data',
          status: 'error',
          description: 'Please select a valid date and timezone',
        });
        return false;
      }

      const params = {
        ...detailsRef.current.values,
        ...(scorecardRequired ? scorecardRef.current.values : {}),
        scorecard_required: scorecardRequired,
        schedule_at: {
          formattedTime: scheduleAt.formattedTime,
          timezoneValue: {
            key: scheduleAt.timezoneValue.key,
          },
        },
      };

      if (isAdhoc && !assessmentData?.id) {
        return onCreateAndSend(params);
      }

      // Handle standard flow
      return handleScheduleSubmit({
        assessmentId: assessmentData?.id,
        formData: params,
        scheduleData,
        isCreateFlow: assessmentData?.status === 'pending',
        toast,
        scheduleAssessment,
        refetch,
        refetchAssessmentData,
        onClose,
      });
    } catch (error) {
      toast({
        title: 'Failed to schedule assessment',
        status: 'error',
        description: error.message,
      });
      return false;
    }
  };

  const handleCancelScheduleAction = async () => {
    // For adhoc without assessmentData, just clear the schedule ref
    if (isAdhoc && !assessmentData?.id) {
      scheduleRef.current?.resetForm();
      return true;
    }

    return handleCancelSchedule({
      assessmentId: assessmentData?.id,
      scheduleRef,
      toast,
      cancelScheduledAssessment,
      refetchAssessmentData,
    });
  };

  return (
    <>
      <Flex flexDir="column" h="100%" overflowY="hidden">
        <Tabs
          display="flex"
          flexDir="column"
          flex="1"
          h="100%"
          overflowY="hidden"
          alignItems="stretch"
          index={assessmentTabs?.findIndex(
            (option) => option?.id === currentTab
          )}
        >
          <TabList pl="24px">
            {assessmentTabs.map((option, idx) => (
              <CustomTabWithStep
                key={option?.id}
                label={option?.label}
                step={isCreateFlow ? String(idx + 1) : undefined}
                arrow={isCreateFlow && option?.id !== 'scorecard'}
                onClick={async () => {
                  const currentTabObj = assessmentTabs.find(
                    (tab) => tab.id === currentTab
                  );
                  if (isCreateFlow && !tabVisited?.includes(option?.id)) return;

                  // Handle Error on tab change
                  let isEmailError = false;
                  if (currentTab === 'email') {
                    isEmailError = getEmailHasError();
                  }
                  const isScorecardSkippable =
                    currentTab === 'scorecard' && !scorecardRequired;
                  if (!isScorecardSkippable) {
                    await currentTabObj?.props?.formRef?.current?.submitForm();
                    if (
                      !currentTabObj?.props?.formRef?.current?.isValid ||
                      isEmailError
                    )
                      return;
                  }
                  setCurrentTab(option?.id);
                }}
              />
            ))}
          </TabList>
          <TabPanels maxH="calc(100% - 40px)">
            {assessmentTabs.map((option) => {
              const Component = option.component;
              return (
                <TabPanel
                  p="0"
                  key={option?.id}
                  maxH="100%"
                  flex="1"
                  overflowY="auto"
                >
                  <Component {...option?.props} activity={assessmentData} />
                  {currentTab === 'scorecard' &&
                    assessmentData?.status === 'scheduled' && (
                      <ScheduleForLater
                        scheduleRef={scheduleRef}
                        scheduleDetails={assessmentData}
                        onSchedule={handleScheduleAction}
                        onCancelSchedule={handleCancelScheduleAction}
                        isScheduling={isScheduling}
                        isCanceling={isCancelScheduled}
                      />
                    )}
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
        <Flex
          p="lg"
          borderTop="1px solid"
          borderColor="border-color"
          bg="ats-secondary"
        >
          {currentTab === 'scorecard' && (
            <Button
              variant="secondary"
              onClick={() => setCurrentTab('details')}
              data-test-id="tertiary-cta-button"
            >
              Back
            </Button>
          )}
          <Flex ml="auto" gap="12px">
            {currentTab === 'scorecard' &&
              (assessmentData?.status === 'pending' ||
                (isAdhoc && !assessmentData?.id)) && (
                <ScheduleForLater
                  scheduleRef={scheduleRef}
                  scheduleDetails={assessmentData}
                  onSchedule={handleScheduleAction}
                  onCancelSchedule={handleCancelScheduleAction}
                  isScheduling={isScheduling}
                  isCanceling={isCancelScheduled}
                />
              )}

            <Button
              variant="primary"
              onClick={clickHandler}
              isLoading={
                isSending || isUpdating || isScheduling || isCancelScheduled
              }
              data-test-id="primary-cta-button"
            >
              {currentTab !== 'scorecard'
                ? `Next: ${getNextTab()?.label}`
                : isAdhoc
                  ? 'Send assessment'
                  : isCreateFlow
                    ? 'Send assessment'
                    : 'Save'}
            </Button>
          </Flex>
        </Flex>
      </Flex>
      <ModalWrapper
        isOpen={confirmActions?.isOpen}
        onClose={confirmActions?.onClose}
        headerText="Updating this assessment will send an email to the candidate"
        subHeaderText="Saving your changes will send an email to the candidate. Are you sure you want to continue?"
        primaryCtaText="Confirm"
        handleSubmit={handleModalSubmit}
        modalContentProps={{ borderRadius: 'sm', pt: '12px', pb: '10px' }}
        isCtaLoading={isUpdating}
      />
    </>
  );
};
